import { useEffect, useRef, useState } from 'react';
import { Empty, Typography } from '@arco-design/web-react';
import { fmtSpan, maskPhone } from '../../utils/format';
import type { TranscriptSegment } from '../../api/cdr';

/**
 * 转写段稳定键：有库主键用 id（通话证据 seg_id 同源），无 id（会话消息段）回落开始毫秒（证据 begin_ms 同源）。
 * 补检采纳与报告定位必须用同一口径，否则会话证据回跳匹配失败。
 */
export function transcriptSegKey(s: TranscriptSegment): string {
  return String(s.id || s.start_ms);
}

/**
 * 转写视图（微信式左右气泡）：segments 为 [{ speaker, content, start_ms, end_ms }]。
 * 主叫（customer）靠左白色气泡，被叫侧（agent/robot）靠右绿色气泡，标签行显示说话人 + 说话起止。
 * 顺序按「开始说话时刻」：后端已按 start_ms 排序（双方抢话时按开口先后，而非按谁先说完），
 * 前端不再做 displayMs 单调钳位——钳位会把真实起止顶到上一句之后，与显示的时间对不上。
 * 起止同一时刻（±10ms，含按键/无时长哨兵）只显示一个时间，见 utils/format.ts fmtSpan。
 */
export function TranscriptView({
  transcript,
  caller,
  callee,
  direction,
  pickable,
  marks,
  onPick,
  focusKey,
  focusTick,
  fill,
}: {
  transcript?: TranscriptSegment[] | string | null;
  caller?: string;
  callee?: string;
  direction?: string;
  /** 证据采纳态：气泡可点选采纳（补检工作台用；不传则保持只读） */
  pickable?: boolean;
  /** 已标记气泡：键 → 标签（补检工作台传违规点序号），命中气泡高亮并显示「违规点 N」 */
  marks?: Record<string, string>;
  onPick?: (seg: TranscriptSegment, key: string) => void;
  /** 违规项证据定位：命中时滚动到对应气泡并高亮（通话走 TranscriptPlayer，此处服务会话/无录音场景） */
  focusKey?: string | number | null;
  focusTick?: number;
  /** 撑满父容器高度（人工补检左栏用）：气泡列表自适应高度并内部滚动；父容器须为确定高度 */
  fill?: boolean;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [focusActive, setFocusActive] = useState<string | null>(null);

  // 证据定位：按稳定键命中气泡后滚入可视区并高亮（focusTick 变化时允许重复定位同一句）
  useEffect(() => {
    if (focusKey == null) return;
    const segs = Array.isArray(transcript) ? transcript : [];
    const key = String(focusKey);
    if (!segs.some((s) => transcriptSegKey(s) === key)) return;
    setFocusActive(key);
    listRef.current?.querySelector(`[data-k="${key}"]`)?.scrollIntoView({ block: 'center' });
  }, [focusKey, focusTick, transcript]);

  if (typeof transcript === 'string') {
    return transcript ? (
      <Typography.Paragraph
        style={{ whiteSpace: 'pre-wrap', maxHeight: 420, overflowY: 'auto', marginBottom: 0 }}
      >
        {transcript}
      </Typography.Paragraph>
    ) : (
      <Empty description="暂无转写文本" />
    );
  }
  const segments: TranscriptSegment[] = Array.isArray(transcript) ? transcript : [];
  if (segments.length === 0) {
    return <Empty description="暂无转写文本" />;
  }
  // 说话人 ↔ 号码映射按呼叫方向区分：呼入 客户=主叫/坐席=被叫；外呼反之。
  const customerNum = direction === 'outbound' ? callee : caller;
  const agentNum = direction === 'outbound' ? caller : callee;
  const labelOf = (speaker: string) =>
    speaker === 'customer'
      ? maskPhone(customerNum) || '客户'
      : speaker === 'agent'
        ? `坐席${agentNum ? ` ${maskPhone(agentNum)}` : ''}`
        : speaker === 'system'
          ? '满意度评分'
          : '机器人';
  return (
    <div
      ref={listRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '4px 0',
        maxHeight: fill ? undefined : 420,
        height: fill ? '100%' : undefined,
        minHeight: fill ? 0 : undefined,
        overflowY: 'auto',
      }}
    >
      {segments.map((s) => {
        const speaker = s.speaker || 'customer';
        const isCustomer = speaker === 'customer';
        const span = fmtSpan(s.start_ms, s.end_ms);
        const key = transcriptSegKey(s);
        const isFocus = focusActive === key;
        const mark = marks?.[key];
        const isPicked = mark != null;
        return (
          <div
            key={key}
            data-k={key}
            onClick={pickable ? () => onPick?.(s, key) : undefined}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isCustomer ? 'flex-start' : 'flex-end',
              cursor: pickable ? 'pointer' : undefined,
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginBottom: 2 }}>
              {labelOf(speaker)}
              {span ? ` · ${span}` : ''}
              {isPicked && (
                <Typography.Text type="success" style={{ fontSize: 12, marginLeft: 6 }}>
                  违规点 {mark}
                </Typography.Text>
              )}
            </div>
            <div
              style={{
                maxWidth: '80%',
                padding: '7px 10px',
                borderRadius: isCustomer ? '2px 12px 12px 12px' : '12px 2px 12px 12px',
                background: isCustomer ? 'var(--color-bg-2)' : 'var(--bcc-bubble-remote-bg)',
                border: isCustomer ? '1px solid var(--color-border-2)' : 'none',
                color: isCustomer ? 'var(--color-text-1)' : 'var(--bcc-bubble-remote-text)',
                boxShadow: 'var(--bcc-shadow-card)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 13,
                lineHeight: 1.6,
                outline: isFocus || isPicked ? '2px solid rgb(var(--primary-6))' : undefined,
                outlineOffset: 1,
              }}
            >
              {s.content || ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}
